// src/Homepage/Footer.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, Truck, RotateCcw, Headphones,
  PhoneCall, MessageSquare, MapPin, CreditCard
} from "lucide-react";

export default function Footer() {
  const navigate = useNavigate();

  return (
    <footer className="w-full font-sans select-none">

      {/* TRUST FEATURES BAR */}
      <div className="bg-[#4a0606] text-white py-6 border-b border-[#320404]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-6 text-center md:text-left">

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="text-yellow-400 p-1.5 bg-white/5 rounded-lg">
              <ShieldCheck size={26} />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-white">100% Original Products</p>
              <p className="text-[10px] text-gray-300 font-semibold mt-0.5">Sivakasi Guarantee</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="text-yellow-400 p-1.5 bg-white/5 rounded-lg">
              <CreditCard size={26} />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-white">Safe & Secure Payments</p>
              <p className="text-[10px] text-gray-300 font-semibold mt-0.5">Multiple Payment Options</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="text-yellow-400 p-1.5 bg-white/5 rounded-lg">
              <Truck size={26} />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-white">Pan India Delivery</p>
              <p className="text-[10px] text-gray-300 font-semibold mt-0.5">Fast & Reliable Shipping</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="text-yellow-400 p-1.5 bg-white/5 rounded-lg">
              <RotateCcw size={26} />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-white">Easy Returns</p>
              <p className="text-[10px] text-gray-300 font-semibold mt-0.5">Hassle-Free Returns</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3 col-span-2 md:col-span-1">
            <div className="text-yellow-400 p-1.5 bg-white/5 rounded-lg">
              <Headphones size={26} />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-white">Customer Support</p>
              <p className="text-[10px] text-gray-300 font-semibold mt-0.5">24/7 Support</p>
            </div>
          </div>

        </div>
      </div>

      {/* CORE FOOTER LINKS */}
      <div className="bg-[#5c0808] text-red-100/90 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2.5">
              <span className="text-3xl">🎇</span>
              <span className="text-2xl font-black tracking-wide text-white font-serif">
                மகிழ்யா பட்டாசுகள்
              </span>
            </div>
            <p className="text-xs sm:text-sm text-red-200/70 leading-relaxed font-semibold">
              Premium wholesale firecrackers direct from Sivakasi. Enjoy top quality, safest formulations, and unparalleled festive celebrations.
            </p>
            <div className="flex items-center gap-2 text-[10px] text-yellow-400 font-black uppercase bg-[#450505] p-3 rounded-xl border border-[#791515] w-fit">
              <span>★ 100% Sivakasi Certified Fireworks</span>
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-wider text-white mb-4 border-b border-[#771111] pb-2">
              Quick Navigation
            </h4>
            <ul className="space-y-3 text-xs sm:text-sm font-bold">
              {[
                { name: "Festive Products", path: "/" },
                { name: "Safety Advice Tips", path: "/safetips" },
                { name: "About Our Firm", path: "/about" },
                { name: "Reach Contact Us", path: "/contact" },
                { name: "Wholesale Admin Dashboard", path: "/admin" }
              ].map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => {
                      navigate(link.path);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="hover:text-yellow-300 hover:translate-x-1 transition-all duration-200 text-left"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info Column */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-wider text-white mb-4 border-b border-[#771111] pb-2">
              Wholesale Helpline
            </h4>
            <ul className="space-y-3 text-xs sm:text-sm font-semibold">
              <li className="flex items-start gap-2.5">
                <PhoneCall size={16} className="text-yellow-400 mt-1 shrink-0" />
                <div>
                  <p className="font-bold text-white">Call Support</p>
                  <a href="tel:+919728622667" className="hover:underline text-red-200/80 font-semibold">+91 9728622667</a>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MessageSquare size={16} className="text-green-400 mt-1 shrink-0" />
                <div>
                  <p className="font-bold text-white">WhatsApp Chat</p>
                  <a href="https://wa.me/919728622667" target="_blank" rel="noopener noreferrer" className="hover:underline text-red-200/80 font-semibold">+91 9728622667</a>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin size={16} className="text-yellow-400 mt-1 shrink-0" />
                <div>
                  <p className="font-bold text-white">Warehouse Location</p>
                  <span className="text-red-200/70">Sivakasi, Tamil Nadu, India</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Security & Partners Column */}
          <div className="space-y-4">
            <h4 className="text-xs font-black uppercase tracking-wider text-white mb-4 border-b border-[#771111] pb-2">
              Secure Shopping
            </h4>
            <p className="text-xs text-red-200/60 leading-relaxed font-semibold">
              We support multiple payment channels including UPI, Cards, NetBanking, and Cash on Delivery for absolute peace of mind.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {["COD", "UPI", "RuPay", "Visa", "MasterCard"].map((badge) => (
                <span key={badge} className="px-2.5 py-1 bg-[#450505] border border-[#6b0f0f] text-[10px] font-black rounded-lg text-white select-none">
                  {badge}
                </span>
              ))}
            </div>
          </div>

        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-6 border-t border-[#771111] flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-red-200/50">
          <p>© 2026 மகிழ்யா பட்டாசுகள் Store. All Rights Reserved. Sivakasi, TN.</p>
          <p className="font-semibold text-yellow-500/85">
            Designed & Developed by <a href="https://smartgonext.com" target="_blank" rel="noopener noreferrer" className="hover:underline font-bold text-yellow-400">SmartGoNext</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
